import { useAuth0 } from '@auth0/auth0-react'
import { TermsOfServicePrivacyPolicyModal } from '@purepm/portals-ui'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAcceptTermsAndConditions } from '../api/mutations/resident'
import {
  useGetActivePrivacyPolicyVersion,
  useGetActiveTermsOfServiceVersion,
  useGetTermsAndConditionsAcceptance,
} from '../api/queries/resident'

/**
 * Component that manages the Terms of Service and Privacy Policy acceptance flow.
 * Displays a modal when user needs to accept new versions and handles acceptance/decline.
 */
const TermsOfServiceHandler = () => {
  const { logout, user } = useAuth0()
  const [showModal, setShowModal] = useState(false)

  /** Queries */
  const {
    data: termsAndConditionsAcceptance,
    isLoading: isLoadingTermsAndConditionsAcceptance,
    refetch: refetchTermsAndConditionsAcceptance,
  } = useGetTermsAndConditionsAcceptance()

  const {
    data: activeTermsOfServiceVersion,
    isLoading: isLoadingActiveTermsOfServiceVersion,
  } = useGetActiveTermsOfServiceVersion()

  const {
    data: activePrivacyPolicyVersion,
    isLoading: isLoadingActivePrivacyPolicyVersion,
  } = useGetActivePrivacyPolicyVersion()

  /** Mutations */
  const {
    mutate: acceptTermsAndConditions,
    isPending: isAcceptingTermsAndConditions,
  } = useAcceptTermsAndConditions({
    onSuccess: () => {
      setShowModal(false)
    },
  })

  // Get HTML strings directly from version_text
  const termsOfServiceHtml = activeTermsOfServiceVersion?.version_text || ''
  const privacyPolicyHtml = activePrivacyPolicyVersion?.version_text || ''

  // Memoize version IDs for comparison
  const activeVersionIds = useMemo(
    () => ({
      termsOfService: activeTermsOfServiceVersion?.terms_of_service_version_id,
      paymentPolicy: activePrivacyPolicyVersion?.privacy_policy_version_id,
    }),
    [
      activeTermsOfServiceVersion?.terms_of_service_version_id,
      activePrivacyPolicyVersion?.privacy_policy_version_id,
    ],
  )

  // Check if user needs to accept new terms
  const needsToAcceptTerms = useMemo(() => {
    // If we don't have active version data yet, don't show modal
    if (!activeVersionIds.termsOfService || !activeVersionIds.paymentPolicy) {
      return false
    }

    // If user hasn't accepted terms yet, they need to accept
    if (!termsAndConditionsAcceptance) {
      return true
    }

    // Check if user needs to accept newer versions
    return (
      termsAndConditionsAcceptance.terms_of_service_version_id !==
        activeVersionIds.termsOfService ||
      termsAndConditionsAcceptance.privacy_policy_version_id !==
        activeVersionIds.paymentPolicy
    )
  }, [termsAndConditionsAcceptance, activeVersionIds])

  // Memoize the modal visibility condition
  const shouldShowModal = useMemo(() => {
    return (
      showModal &&
      termsOfServiceHtml &&
      privacyPolicyHtml &&
      !isLoadingTermsAndConditionsAcceptance &&
      !isLoadingActiveTermsOfServiceVersion &&
      !isLoadingActivePrivacyPolicyVersion
    )
  }, [
    showModal,
    termsOfServiceHtml,
    privacyPolicyHtml,
    isLoadingTermsAndConditionsAcceptance,
    isLoadingActiveTermsOfServiceVersion,
    isLoadingActivePrivacyPolicyVersion,
  ])

  /** Handlers */
  const handleDecline = useCallback(() => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }, [logout])

  const handleAccept = useCallback(() => {
    if (!activeVersionIds.termsOfService || !activeVersionIds.paymentPolicy) {
      console.warn('Missing version IDs for terms acceptance')
      return
    }

    acceptTermsAndConditions({
      termsOfServiceVersionId: activeVersionIds.termsOfService,
      privacyPolicyVersionId: activeVersionIds.paymentPolicy,
    })
  }, [acceptTermsAndConditions, activeVersionIds])

  // Refetch terms acceptance when user changes
  useEffect(() => {
    if (user) {
      refetchTermsAndConditionsAcceptance()
    }
  }, [user, refetchTermsAndConditionsAcceptance])

  // Show modal when user needs to accept new terms
  useEffect(() => {
    setShowModal(needsToAcceptTerms)
  }, [needsToAcceptTerms])

  if (!shouldShowModal) return null

  return (
    <TermsOfServicePrivacyPolicyModal
      isOpen
      termsOfServiceHtml={termsOfServiceHtml}
      privacyPolicyHtml={privacyPolicyHtml}
      onDecline={handleDecline}
      onAccept={handleAccept}
      isLoading={isAcceptingTermsAndConditions}
    />
  )
}

export default TermsOfServiceHandler
